import {Checkout, CheckoutFactory} from "../../../typescript-library";
import * as React from "react";
import {useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {getShopId} from "../../lib/getShopId";
import {Payments} from "../../lib/payments/PaymentController";
import {PayPalLoadingSpinner} from "../../lib/payments/paypal/BraintreeButton";

interface ICheckoutContext {
  checkout: Checkout;
}

const checkoutContext = React.createContext<ICheckoutContext | null>(null);

export const useCheckoutContext = (): ICheckoutContext => {
  const context = React.useContext(checkoutContext);
  if (!context) {
    throw new Error("useCheckoutContext must be used in a component within a CheckoutContextProvider.");
  }

  return context;
};

interface ICheckoutContextProviderState {
  isLoading: boolean;
  checkoutContext: ICheckoutContext | null;
}

const CheckoutContextProvider: React.FC<{children?: React.ReactNode}> = (props) => {
  const [state, setState] = React.useState<ICheckoutContextProviderState>({checkoutContext: null, isLoading: true});
  const {cart, viewState} = useCartContext();
  const handleCheckoutEmitterEvents = () => {
    setState((prevState) => {
      if (!prevState) {
        // this might be an error case
        return {checkoutContext: null, isLoading: true};
      }

      return {checkoutContext: prevState.checkoutContext, isLoading: false};
    });
  };
  const checkoutRef = React.useRef<Checkout | null>(null);

  React.useEffect(() => {
    void CheckoutFactory.generateCheckout(getShopId(), cart).then((checkout) => {
      let wait = false;
      const setThings = () => {
        if (wait) {
          return;
        }
        setState({checkoutContext: {checkout}, isLoading: false});
        checkoutRef.current = checkout;
        checkout.emitter.on(handleCheckoutEmitterEvents);
      };

      if (checkout.paymentId) {
        if (
          (checkout.paymentId === Payments.PaypalExpressBraintree && !viewState.paypalInfos) ||
          (checkout.paymentId === Payments.ApplePay && !viewState.appleData) ||
          (checkout.paymentId === Payments.GooglePay && !viewState.gPayData)
        ) {
          wait = true;
          void checkout.put({paymentId: null}).then(() => {
            wait = false;
            setThings();
          });
        }
      }

      setThings();
    });

    return () => {
      if (!checkoutRef.current) {
        return;
      }
      checkoutRef.current.emitter.off(handleCheckoutEmitterEvents);
    };
  }, [cart.cartId]);

  if (state.isLoading || !state.checkoutContext) {
    return <PayPalLoadingSpinner />;
  }

  const {Provider} = checkoutContext;

  return <Provider value={state.checkoutContext}>{props.children}</Provider>;
};

export default CheckoutContextProvider;
